import { isKagglePro } from "@kaggle/components";
import { featureFlags, Kaggle } from "@kaggle/sdk";
import { defaultTheme } from "@kaggle/styled-components";
import { SessionState as StyledSessionState } from "@kaggle/styled-components/lib/types";
import { createSelector } from "reselect";
import { ViewType } from "../../types/ViewType";
import { SharedState } from "../types/SharedState";
import { getIsOwner } from "./collaborators";
import { getIsAdmin, getIsAnonymous } from "./user";

import EditorType = Kaggle.Sdk.Kernels.EditorType;

type UserPlan = Kaggle.Sdk.Users.UserPlan;

/** Selects the session state from the parent frame. */
export const getSessionState = (state: SharedState) => state.session;

/** Selects the source type of "script" or "notebook" in the current session. */
export const getSourceType = createSelector(
  [getSessionState],
  session => session.sourceType
);

export const getUserPlan = createSelector(
  [getSessionState],
  session => session.user?.userPlan
);

export const getStyledSessionState = createSelector(
  [getSessionState],
  (session): StyledSessionState => ({
    sourceType: getSourceTypeFromEditorType(session.sourceType),
    themeName: session.themeName,
    experimentalTheme: (session.user && session.user.role === "admin") || false,
  })
);

function getSourceTypeFromEditorType(editorType: EditorType) {
  switch (editorType) {
    case EditorType.SCRIPT:
      return "script";
    case EditorType.NOTEBOOK:
      return "notebook";
    default:
      return "unspecified";
  }
}

/** In the future this should switch themes in theme provider. */
export const getTheme = createSelector([getStyledSessionState], session => ({
  ...defaultTheme,
  session,
  name: session.themeName,
}));

/** Gets the editor settings from state. */
export const getDeviceSettings = createSelector(
  [getSessionState],
  session => session.settings
);

/** Gets the editor settings view (visibility) from state. */
export const getDeviceSettingsEnabledViews = createSelector(
  [getDeviceSettings],
  settings => settings.enabledViews
);

/** Gets the editor settings view (visibility) from state. */
export const getDeviceSettingsExpandedViews = createSelector(
  [getDeviceSettings],
  settings => settings.expandedViews
);

/** Gets whether the data pane is visible in sidebar. */
export const getIsDataPaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews],
  views => views.includes(ViewType.DATA_PANE)
);

/** Gets whether the data pane is expanded in sidebar. */
export const getIsDataPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.DATA_PANE)
);

/** Gets whether the competitions pane is visible in sidebar. */
export const getIsCompetitionsPaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews, getIsAnonymous],
  (views, isAnonymous) =>
    featureFlags.kernelsSubmitFromEditor &&
    !isAnonymous &&
    views.includes(ViewType.COMPETITIONS_PANE)
);

/** Gets whether the competitions pane is expanded in sidebar. */
export const getIsCompetitionsPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews, getIsAnonymous],
  (views, isAnonymous) =>
    featureFlags.kernelsSubmitFromEditor &&
    !isAnonymous &&
    views.includes(ViewType.COMPETITIONS_PANE)
);

/** Gets whether the settings pane is visible in sidebar. */
export const getIsSettingsPaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews],
  views => views.includes(ViewType.SETTINGS_PANE)
);

/** Gets whether the settings pane is expanded in sidebar. */
export const getIsSettingsPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.SETTINGS_PANE)
);

/** Gets whether the debug pane is visible in sidebar. */
export const getIsDebugPaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews, getIsAdmin],
  (views, isAdmin) => isAdmin && views.includes(ViewType.DEBUG_PANE)
);

/** Gets whether the debug pane is expanded in sidebar. */
export const getIsDebugPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.DEBUG_PANE)
);

export const getIsInteractiveSessionsExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.INTERACTIVE_SESSIONS)
);

export const getIsCommitSessionsExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.COMMIT_SESSIONS)
);

/** Gets whether the sidebar should be expanded or not. */
export const getIsSidebarExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.SIDEBAR)
);

/** Gets whether the debug pane is visible in sidebar. */
export const getIsJupyterDevToolsEnabled = createSelector(
  [getDeviceSettingsEnabledViews],
  views => views.includes(ViewType.JUPYTER_DEV_TOOLS)
);

/** Gets whether the snippets pane is visible in sidebar. */
export const getIsSnippetsPaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews],
  _ => true
);

/** Gets whether the snippets pane is expanded or not. */
export const getIsSnippetsPaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views => views.includes(ViewType.SNIPPETS_PANE)
);

/** Gets whether the schedule pane is visible in sidebar. */
export const getIsSchedulePaneEnabled = createSelector(
  [getDeviceSettingsEnabledViews],
  views =>
    featureFlags.scheduledNotebooks && views.includes(ViewType.SCHEDULE_PANE)
);

/** Gets whether the schedule pane is expanded or not. */
export const getIsSchedulePaneExpanded = createSelector(
  [getDeviceSettingsExpandedViews],
  views =>
    featureFlags.scheduledNotebooks && views.includes(ViewType.SCHEDULE_PANE)
);

/** Gets the state of sidebar panes collectively and controls their ordering. */
export const getSidebarPanes = createSelector(
  [
    getIsDataPaneEnabled,
    getIsCompetitionsPaneEnabled,
    getIsSettingsPaneEnabled,
    getIsDebugPaneEnabled,
    getIsSnippetsPaneEnabled,
    getIsSchedulePaneEnabled,
    getIsDataPaneExpanded,
    getIsCompetitionsPaneExpanded,
    getIsSettingsPaneExpanded,
    getIsDebugPaneExpanded,
    getIsSnippetsPaneExpanded,
    getIsSchedulePaneExpanded,
    getUserPlan,
    getIsOwner,
  ],
  (
    isDataPaneEnabled: boolean,
    isCompetitionsPaneEnabled: boolean,
    isSettingsEnabled: boolean,
    isDebugEnabled: boolean,
    isSnippetsEnabled: boolean,
    isScheduleEnabled: boolean,
    isDataPaneExpanded: boolean,
    isCompetitionsPaneExpanded: boolean,
    isSettingsExpanded: boolean,
    isDebugExpanded: boolean,
    isSnippetsExpanded: boolean,
    isScheduleExpanded: boolean,
    userPlan: UserPlan | undefined,
    isOwner: boolean
  ) =>
    [
      isDataPaneEnabled && {
        viewType: ViewType.DATA_PANE,
        title: "Data",
        isExpanded: isDataPaneExpanded,
      },
      isCompetitionsPaneEnabled && {
        viewType: ViewType.COMPETITIONS_PANE,
        title: "Competitions",
        isExpanded: isCompetitionsPaneExpanded,
      },
      isSettingsEnabled && {
        viewType: ViewType.SETTINGS_PANE,
        title: `Settings${isKagglePro(userPlan) ? " (Kaggle Pro)" : ""}`,
        isExpanded: isSettingsExpanded,
      },
      isScheduleEnabled &&
        isOwner && {
          viewType: ViewType.SCHEDULE_PANE,
          title: "Schedule a notebook run",
          isExpanded: isScheduleExpanded,
        },
      isDebugEnabled && {
        viewType: ViewType.DEBUG_PANE,
        title: "Debug",
        isExpanded: isDebugExpanded,
      },
      isSnippetsEnabled && {
        viewType: ViewType.SNIPPETS_PANE,
        title: "Code Help",
        isExpanded: isSnippetsExpanded,
      },
    ].filter(metadata => !!metadata)
);
